import { observer, useViewModel } from '@descomplica/microfrontend-react';
import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { InputPassword } from '@/modules/recognition/presentation/components/InputPassword';
import { Button } from '@/shared/presentation/components/ui/Button';
import { FormErrorMessage } from '@/shared/presentation/components/ui/ErrorMessage';
import { Space } from '@/shared/presentation/components/ui/Space';

import LogoSvg from '../../../../assets/logo.svg';
import { ResetPassViewModel } from './ResetPass.vm';

const Container = styled.div`
  max-width: 100vw;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: var(--gray-02);

  @media screen and (max-width: 480px) {
    background-color: white;

    button:first-child {
      width: 100%;
    }
  }
`;

const Content = styled.div`
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  background-color: var(--white);
  border-radius: 16px;
  color: #000;
  padding: 48px;

  @media screen and (max-width: 480px) {
    padding: 24px;
    height: 100vh;
    border-radius: 0;
    max-width: calc(100vw - 48px);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;

  &:has(button) {
    align-items: normal;
  }
`;

const Title = styled.h1`
  font-size: 32px;
  line-height: 1.2;
  color: var(--gray-90);
  margin-bottom: 8px;
`;

const Subtitle = styled.div`
  font-size: 16px;
  line-height: 20px;
  color: #636769;
  font-weight: normal;
  margin-bottom: 8px;
`;

const Logo = styled.img.attrs({
  src: `${LogoSvg}`,
})`
  width: auto;
  max-width: 128px;
  margin: 0 auto 24px;
  display: block;
`;

type Props = Partial<{
  title?: string;
  onClose?: () => void;
}>;

export const ResetPass: React.FC<Props> = observer(() => {
  const navigate = useNavigate();
  const vm = useViewModel(ResetPassViewModel, { navigate });

  const resetPass = (e) => {
    e.preventDefault();
    vm.resetPass();
  };

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('token');
    if (!token) navigate('/signin');
  }, []);

  return (
    <Container>
      <Content>
        <Link to="/">
          <Logo />
        </Link>
        <Title>Definir Nova Senha</Title>
        <Subtitle>
          Informe a sua nova senha. Agora você irá usá-la para ter acesso ao Descomplica.IO
        </Subtitle>
        <Space height={16} />
        <Form onSubmit={resetPass}>
          {vm.errorMessage && <FormErrorMessage>{vm.errorMessage}</FormErrorMessage>}
          <InputPassword
            label="Nova Senha"
            placeholder="Digite sua nova senha"
            name="senha"
            type="password"
            errorMessage={vm.form.errors.newPassword && vm.form.errors.newPassword[0]}
            onChange={(value) => vm.form.setValue('newPassword', value)}
            onBlur={() => vm.form.validateField('newPassword')}
          />
          <Space height={16} />
          <InputPassword
            label="Confirmação de Senha"
            placeholder="Confirme sua nova senha"
            name="confirmacao_de_senha"
            type="password"
            errorMessage={
              vm.form.errors.newPasswordConfirmation && vm.form.errors.newPasswordConfirmation[0]
            }
            onChange={(value) => vm.form.setValue('newPasswordConfirmation', value)}
            onBlur={() => vm.form.validateField('newPasswordConfirmation')}
          />
          <Space height={24} />
          <Button disabled={!vm.form.isValid || vm.isSubmitting} loading={vm.isSubmitting}>
            Trocar Senha
          </Button>
        </Form>
      </Content>
    </Container>
  );
});
